/**
 * Permission Helper Utilities
 * These functions check admin permissions and roles
 */

/**
 * Get stored admin profile from localStorage
 */
export const getStoredAdmin = () => {
  try {
    const adminProfile = localStorage.getItem("adminProfile");
    if (adminProfile) {
      return JSON.parse(adminProfile);
    }
    return null;
  } catch (error) {
    console.error("Error parsing admin profile:", error);
    return null;
  }
};

/**
 * Check if admin has permission for a specific resource and action
 * @param {string} resource - Resource name (estates, users, security, visitors, broadcasts, dues, reports)
 * @param {string} action - Action name (create, read, update, delete, export)
 * @returns {boolean}
 */
export const can = (resource, action, adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();

  if (!admin) {
    return false;
  }

  // Platform super admins have all permissions
  if (admin.role === "platform_super_admin") {
    return true;
  }

  // Check permissions object
  const permissions = admin.permissions;
  if (!permissions || !permissions[resource]) {
    return false;
  }

  return permissions[resource][action] === true;
};

/**
 * Check if current admin is Platform Super Admin
 * @returns {boolean}
 */
export const isPlatformSuperAdmin = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return (
    admin?.role === "platform_super_admin" || admin?.accountType === "platform"
  );
};

/**
 * Check if current admin is Estate Super Admin
 * @returns {boolean}
 */
export const isEstateSuperAdmin = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return admin?.role === "estate_super_admin";
};

/**
 * Check if current admin is Estate Admin (with role restrictions)
 * @returns {boolean}
 */
export const isEstateAdmin = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return admin?.role === "estate_admin";
};

/**
 * Get estate scope (list of estates admin can access)
 * @returns {Array<string>}
 */
export const getEstateScope = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();

  if (!admin) {
    return [];
  }

  // Platform super admins can access all estates
  if (isPlatformSuperAdmin(admin)) {
    return ["all"];
  }

  return admin.estateScope || admin.managedEstates || [];
};

/**
 * Get managed estates (for super admins who manage multiple estates)
 * @returns {Array<string>}
 */
export const getManagedEstates = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return admin?.managedEstates || [];
};

/**
 * Get current estate ID
 * @returns {string|null}
 */
export const getCurrentEstate = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return admin?.estate || null;
};

/**
 * Get estate role information
 * @returns {Object|null}
 */
export const getEstateRole = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();
  return admin?.estateRole || null;
};

/**
 * Check if admin can access a specific estate
 * @param {string} estateId - Estate ID to check
 * @returns {boolean}
 */
export const canAccessEstate = (estateId, adminOverride = null) => {
  if (isPlatformSuperAdmin(adminOverride)) {
    return true;
  }

  const estateScope = getEstateScope(adminOverride);
  return estateScope.includes(estateId);
};

/**
 * Check if admin manages multiple estates
 * @returns {boolean}
 */
export const managesMultipleEstates = (adminOverride = null) => {
  if (isPlatformSuperAdmin(adminOverride)) {
    return true;
  }

  const managedEstates = getManagedEstates(adminOverride);
  return managedEstates.length > 1;
};

/**
 * Get admin role display name
 * @returns {string}
 */
export const getRoleDisplayName = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();

  if (!admin) {
    return "Unknown";
  }

  const roleMap = {
    platform_super_admin: "Platform Super Admin",
    estate_super_admin: "Estate Super Admin",
    estate_admin: admin.estateRole?.name || "Estate Admin",
  };

  return roleMap[admin.role] || admin.role;
};

/**
 * Check multiple permissions at once
 * @param {Object} checks - Object with resource keys and action arrays
 * @returns {boolean}
 *
 * Example: canAll({ users: ['create', 'update'], estates: ['read'] })
 */
export const canAll = (checks) => {
  for (const [resource, actions] of Object.entries(checks)) {
    for (const action of actions) {
      if (!can(resource, action)) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Check if admin has any of the specified permissions
 * @param {Object} checks - Object with resource keys and action arrays
 * @returns {boolean}
 *
 * Example: canAny({ users: ['create', 'update'], estates: ['create'] })
 */
export const canAny = (checks) => {
  for (const [resource, actions] of Object.entries(checks)) {
    for (const action of actions) {
      if (can(resource, action)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Get all permissions for current admin
 * @returns {Object}
 */
export const getAllPermissions = () => {
  const admin = getStoredAdmin();
  return admin?.permissions || {};
};

/**
 * Check if admin has full access to a resource (all CRUD operations)
 * @param {string} resource - Resource name
 * @returns {boolean}
 */
export const hasFullAccess = (resource) => {
  return (
    can(resource, "create") &&
    can(resource, "read") &&
    can(resource, "update") &&
    can(resource, "delete")
  );
};

/**
 * Get admin info for display
 * @returns {Object}
 */
export const getAdminInfo = (adminOverride = null) => {
  const admin = adminOverride || getStoredAdmin();

  if (!admin) {
    return null;
  }

  return {
    id: admin.id,
    name: admin.name,
    email: admin.email,
    username: admin.username,
    role: admin.role,
    roleDisplayName: getRoleDisplayName(admin),
    estate: admin.estate,
    managedEstates: getManagedEstates(admin),
    estateScope: getEstateScope(admin),
    isPlatformSuperAdmin: isPlatformSuperAdmin(admin),
    isEstateSuperAdmin: isEstateSuperAdmin(admin),
    isEstateAdmin: isEstateAdmin(admin),
  };
};

// Export all functions
export default {
  can,
  isPlatformSuperAdmin,
  isEstateSuperAdmin,
  isEstateAdmin,
  getEstateScope,
  getManagedEstates,
  getCurrentEstate,
  getEstateRole,
  canAccessEstate,
  managesMultipleEstates,
  getRoleDisplayName,
  canAll,
  canAny,
  getAllPermissions,
  hasFullAccess,
  getAdminInfo,
  getStoredAdmin,
};
